import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "~/api/errors";
import { useApi } from "~/api/provider";
import type { ReasoningSession } from "~/api/reasoningTypes";
import { Button } from "~/foundation/components/Button";
import {
  type CandidateBase,
  type CandidateCommand,
  type CandidateDraft,
  candidateCommand,
  candidateRecords,
  dependencyDiff,
  draftFrom,
  draftJson,
  evidenceKey,
  evidenceLabel,
  latestCandidate,
  nextRevision,
  parseDraft,
  pinnedEvidence,
  rebaseDraft,
  sessionSnapshotKey,
  validateDraft,
} from "./candidateDraft";
import { ReasoningRecordGraph } from "./ReasoningRecordGraph";
import { useReasoningSupport } from "./useReasoningSupport";

function initialState(session: ReasoningSession, predecessorId: string | null) {
  const base: CandidateBase = { session, predecessorId };
  const draft = draftFrom(base);
  return { base, draft, text: draftJson(draft) };
}

/** A single draft is shared by all three editors. All writes append through the reasoning API. */
export function ReasoningCandidateEditor({
  session,
  disabled = false,
  onSessionChange,
  onDirtyChange,
}: {
  session: ReasoningSession;
  disabled?: boolean;
  onSessionChange: (session: ReasoningSession) => void;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { t } = useTranslation();
  const { reasoning } = useApi();
  const { limits } = useReasoningSupport();
  const [state, setState] = useState(() =>
    initialState(session, candidateRecords(session).at(-1)?.id ?? null),
  );
  const [view, setView] = useState<"Graph" | "Form" | "JSON">("Graph");
  const [inspectedId, setInspectedId] = useState(
    state.base.predecessorId ?? session.contextRecordId,
  );
  const [inspectionOpen, setInspectionOpen] = useState(false);
  const [requestedSelection, setRequestedSelection] = useState<string | null>();
  const [remote, setRemote] = useState<ReasoningSession | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [pending, setPending] = useState<CandidateCommand | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const inFlight = useRef(false);
  const { base, draft, text } = state;
  const parsed = useMemo(() => parseDraft(text), [text]);
  const dirty = text !== draftJson(draftFrom(base));
  const latest = remote && remote.revision >= session.revision ? remote : session;
  const drifted = sessionSnapshotKey(latest) !== sessionSnapshotKey(base.session);
  const errors = [...parsed.errors, ...validateDraft(base, draft, Date.now(), limits)];
  const frozen = busy || disabled || pending !== null || savedId !== null;
  const editDisabled = frozen || parsed.draft === null;
  const inspected = base.session.records.find((record) => record.id === inspectedId);
  const diff = dependencyDiff(base, draft);
  const latestRevision = latestCandidate(latest, draft.logicalId);
  const needsRebase =
    drifted || (base.predecessorId !== null && latestRevision?.id !== base.predecessorId);
  const pinned = pinnedEvidence(base.session);
  const evidenceOptions = [
    ...new Map([...pinned, ...draft.evidence].map((item) => [evidenceKey(item), item])).values(),
  ];
  const changeWord: Record<string, string> = {
    Kept: t("Kept"),
    Added: t("Added"),
    Removed: t("Removed"),
  };

  useEffect(() => {
    onDirtyChange?.(dirty || pending !== null || savedId !== null);
  }, [dirty, pending, savedId, onDirtyChange]);
  useEffect(() => setRemote(session), [session]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  function edit(update: Partial<CandidateDraft>) {
    if (editDisabled) return;
    const next = { ...draft, ...update };
    setState({ ...state, draft: next, text: draftJson(next) });
    setNotice("");
  }
  function changeSelection(id: string | null) {
    if (frozen) return;
    if (dirty) {
      setRequestedSelection(id);
      return;
    }
    loadSelection(id);
  }
  function loadSelection(id: string | null) {
    setState(initialState(latest, id));
    setInspectedId(id ?? latest.contextRecordId);
    setRequestedSelection(undefined);
    setError("");
    setNotice("");
  }
  async function inspectLatest() {
    if (inFlight.current) return;
    setBusy(true);
    setError("");
    try {
      const result = await reasoning.get(session.id);
      setRemote(result.session);
      onSessionChange(result.session);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  async function reloadSaved(id: string) {
    const result = await reasoning.get(session.id);
    if (!result.session.records.some((record) => record.id === id))
      throw new Error(t("Saved record is not visible yet. Reload records to continue."));
    setRemote(result.session);
    setState(initialState(result.session, id));
    setInspectedId(id);
    setSavedId(null);
    onSessionChange(result.session);
  }
  async function save() {
    if (inFlight.current || disabled || savedId) return;
    if (!pending && (errors.length > 0 || drifted || !dirty)) return;
    inFlight.current = true;
    const command = pending ?? candidateCommand(base, draft, crypto.randomUUID());
    setPending(command);
    setBusy(true);
    setError("");
    setNotice("");
    let committed = false;
    try {
      const receipt = await reasoning.candidate(command);
      committed = true;
      if (!receipt.recordId)
        throw new Error(
          t(
            "The save response did not identify its record. Retry the same save to recover its receipt.",
          ),
        );
      setPending(null);
      setSavedId(receipt.recordId);
      setNotice(
        t("Saved {{name}} revision {{revision}}. Evaluate this revision before accepting it.", {
          name: draft.logicalId,
          revision: command.revision,
        }),
      );
      await reloadSaved(receipt.recordId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      if (
        !committed &&
        e instanceof ApiError &&
        e.status >= 400 &&
        e.status < 500 &&
        ![408, 429].includes(e.status)
      ) {
        setPending(null);
        if (e.status === 409) {
          try {
            const result = await reasoning.get(session.id);
            setRemote(result.session);
            onSessionChange(result.session);
          } catch {
            setError(
              t(
                "Save conflicted and latest records could not be loaded. Your draft is retained; load the latest session to review it.",
              ),
            );
          }
        }
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  return (
    <section
      aria-label={t("Reasoning editor")}
      className="flex flex-col gap-3 border-t border-divider pt-4"
    >
      <h4 className="text-lg font-semibold">{t("Reasoning editor")}</h4>
      <p className="text-small text-text-secondary">
        {t(
          "Edit candidate content and its dependencies. Saving creates a new revision and clears current acceptance. Each revision requires evaluation.",
        )}
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <label>
          {t("Candidate revision")}{" "}
          <select
            aria-label={t("Edit candidate revision")}
            disabled={frozen}
            value={base.predecessorId ?? ""}
            onChange={(event) => changeSelection(event.target.value || null)}
            className="rounded border border-divider bg-bg p-1"
          >
            <option value="">{t("New candidate")}</option>
            {candidateRecords(base.session).map((row) => (
              <option key={row.id} value={row.id}>
                {row.logicalId} · r{row.revision}
                {!row.eligible ? ` · ${t("needs review")}` : ""}
              </option>
            ))}
          </select>
        </label>
        <span className="text-small">
          {t("Session revision {{session}} · draft revision {{draft}}", {
            session: base.session.revision,
            draft: nextRevision(base, draft),
          })}
        </span>
        <Button size="sm" disabled={frozen} onClick={() => changeSelection(null)}>
          {t("New candidate")}
        </Button>
        <Button size="sm" disabled={busy || disabled} onClick={inspectLatest}>
          {t("Load latest session")}
        </Button>
      </div>
      {requestedSelection !== undefined && (
        <div role="alert" className="rounded border border-divider p-3">
          <p>{t("This draft has unsaved changes.")}</p>
          <Button disabled={frozen} onClick={() => loadSelection(requestedSelection)}>
            {t("Discard draft and switch")}
          </Button>{" "}
          <Button onClick={() => setRequestedSelection(undefined)}>{t("Keep editing")}</Button>
        </div>
      )}
      {error && (
        <p role="alert" className="text-danger">
          {error}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      {pending && !busy && (
        <p role="alert">
          {t(
            "The save outcome is unknown. Retry the same save before editing; its command identity prevents a duplicate revision.",
          )}
        </p>
      )}
      {savedId && !busy && (
        <Button
          onClick={async () => {
            setBusy(true);
            setError("");
            try {
              await reloadSaved(savedId);
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          {t("Reload saved records")}
        </Button>
      )}
      {needsRebase && (
        <section aria-label={t("Revision conflict")} className="rounded border border-divider p-3">
          <h5 className="font-semibold">
            {t("Session changed: revision {{from}} → {{to}}", {
              from: base.session.revision,
              to: latest.revision,
            })}
          </h5>
          <p>
            {t("Your draft is retained. Compare it with the latest candidate before rebasing.")}
          </p>
          {latest.contextRecordId !== base.session.contextRecordId && (
            <p>{t("Context changed. Review every evidence version after rebasing.")}</p>
          )}
          {latest.criteriaDigest !== base.session.criteriaDigest && (
            <p>{t("Acceptance criteria changed.")}</p>
          )}
          <div className="grid gap-2 md:grid-cols-3">
            <div>
              <strong>{t("Original")}</strong>
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap">
                {String(
                  base.session.records.find((row) => row.id === base.predecessorId)?.payload
                    .content ?? t("New candidate"),
                )}
              </pre>
            </div>
            <div>
              <strong>{t("Your draft")}</strong>
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap">{draft.content}</pre>
            </div>
            <div>
              <strong>{t("Latest saved")}</strong>
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap">
                {String(latestRevision?.payload.content ?? t("No candidate yet"))}
              </pre>
            </div>
          </div>
          <Button
            disabled={editDisabled}
            onClick={() => {
              const next = rebaseDraft(draft, latest);
              setState({ ...next, text: draftJson(next.draft) });
              setError("");
              setNotice(
                t("Draft rebased. Review the dependency changes and validation before saving."),
              );
            }}
          >
            {t("Keep my draft and rebase")}
          </Button>{" "}
          <Button disabled={frozen} onClick={() => changeSelection(latestRevision?.id ?? null)}>
            {t("Use latest saved candidate")}
          </Button>
        </section>
      )}
      <div role="group" aria-label={t("Editor view")} className="flex gap-2">
        {(["Graph", "Form", "JSON"] as const).map((name) => (
          <Button key={name} aria-pressed={view === name} onClick={() => setView(name)}>
            {name === "Graph" ? t("Graph") : name === "Form" ? t("Form") : t("JSON")}
          </Button>
        ))}
      </div>
      {parsed.draft === null && (
        <div role="alert">
          <p>{parsed.errors.join(" ")}</p>
          <Button disabled={frozen} onClick={() => setState({ ...state, text: draftJson(draft) })}>
            {t("Restore valid JSON")}
          </Button>
        </div>
      )}
      {view === "JSON" ? (
        <label className="flex flex-col gap-1">
          {t("Candidate draft JSON")}
          <textarea
            aria-label={t("Candidate draft JSON")}
            spellCheck={false}
            rows={18}
            disabled={frozen}
            value={text}
            onChange={(event) => {
              const value = event.target.value;
              const result = parseDraft(value);
              setState({ ...state, text: value, draft: result.draft ?? draft });
              setNotice("");
            }}
            className="rounded border border-divider bg-bg p-2 font-mono text-small"
          />
        </label>
      ) : (
        <>
          {view === "Graph" && (
            <ReasoningRecordGraph
              base={base}
              draft={draft}
              disabled={editDisabled}
              onParentsChange={(ids) => edit({ derivedFromRecordIds: ids })}
              onInspect={(id) => {
                setInspectedId(id);
                setInspectionOpen(true);
              }}
            />
          )}
          <fieldset disabled={editDisabled} className="flex flex-col gap-3">
            <legend className="font-semibold">{t("Draft content")}</legend>
            <label className="flex flex-col gap-1">
              {t("Candidate name")}
              <input
                aria-label={t("Candidate name")}
                value={draft.logicalId}
                readOnly={base.predecessorId !== null}
                onChange={(event) => edit({ logicalId: event.target.value })}
                className="rounded border border-divider bg-bg p-2"
              />
            </label>
            <label className="flex flex-col gap-1">
              {t("Candidate content")}
              <textarea
                aria-label={t("Candidate content")}
                rows={5}
                value={draft.content}
                onChange={(event) => edit({ content: event.target.value })}
                className="rounded border border-divider bg-bg p-2"
              />
            </label>
            <label className="flex flex-col gap-1">
              {t("Decision summary")}
              <textarea
                aria-label={t("Decision summary")}
                rows={2}
                value={draft.rationale}
                onChange={(event) => edit({ rationale: event.target.value })}
                className="rounded border border-divider bg-bg p-2"
              />
            </label>
            <fieldset className="flex flex-col gap-1">
              <legend className="font-semibold">{t("Candidate parents")}</legend>
              {base.session.records
                .filter((row) => ["candidate", "retained-artifact"].includes(row.kind))
                .map((row) => (
                  <label key={row.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={draft.derivedFromRecordIds.includes(row.id)}
                      disabled={
                        row.id === base.predecessorId ||
                        (!row.eligible && !draft.derivedFromRecordIds.includes(row.id))
                      }
                      onChange={(event) =>
                        edit({
                          derivedFromRecordIds: event.target.checked
                            ? [...draft.derivedFromRecordIds, row.id]
                            : draft.derivedFromRecordIds.filter((id) => id !== row.id),
                        })
                      }
                    />
                    {row.logicalId} · r{row.revision}
                    {row.kind === "retained-artifact" ? ` · ${t("Retained file")}` : ""}
                    {row.id === base.predecessorId ? ` · ${t("required predecessor")}` : ""}
                    {!row.eligible ? ` · ${t("needs review")}` : ""}
                  </label>
                ))}
              {!base.session.records.some((row) =>
                ["candidate", "retained-artifact"].includes(row.kind),
              ) && <p>{t("No earlier candidates.")}</p>}
            </fieldset>
            <fieldset className="flex flex-col gap-1">
              <legend className="font-semibold">{t("Pinned evidence")}</legend>
              {evidenceOptions.map((item) => (
                <label key={evidenceKey(item)} className="flex items-center gap-2 break-all">
                  <input
                    type="checkbox"
                    checked={draft.evidence.some((e) => evidenceKey(e) === evidenceKey(item))}
                    onChange={(event) =>
                      edit({
                        evidence: event.target.checked
                          ? [...draft.evidence, item]
                          : draft.evidence.filter((e) => evidenceKey(e) !== evidenceKey(item)),
                      })
                    }
                  />
                  {evidenceLabel(item)}
                  {!pinned.some((e) => evidenceKey(e) === evidenceKey(item))
                    ? ` · ${t("no longer pinned")}`
                    : ""}
                </label>
              ))}
              {evidenceOptions.length === 0 && (
                <p>{t("No source evidence is pinned in this context.")}</p>
              )}
            </fieldset>
            <label className="flex gap-2">
              <input
                type="checkbox"
                checked={draft.evidenceComplete}
                onChange={(event) => edit({ evidenceComplete: event.target.checked })}
              />
              {t("Evidence coverage is complete")}
            </label>
          </fieldset>
        </>
      )}
      <details
        open={inspectionOpen}
        onToggle={(event) => setInspectionOpen(event.currentTarget.open)}
      >
        <summary>{t("Inspect retained records")}</summary>
        <label>
          {t("Record")}{" "}
          <select
            aria-label={t("Inspect record")}
            value={inspectedId}
            onChange={(event) => setInspectedId(event.target.value)}
          >
            {base.session.records.map((row) => (
              <option key={row.id} value={row.id}>
                {row.kind}: {row.logicalId} · r{row.revision}
              </option>
            ))}
          </select>
        </label>
        {inspected && (
          <>
            <p>
              {inspected.eligible
                ? t("Eligible")
                : t("Needs review: {{reason}}", {
                    reason: inspected.ineligibilityReason ?? t("dependency changed"),
                  })}
            </p>
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded bg-bg-secondary p-2 text-small">
              {JSON.stringify(inspected.payload, null, 2)}
            </pre>
            {inspected.kind === "candidate" && (
              <Button disabled={frozen} onClick={() => changeSelection(inspected.id)}>
                {t("Revise inspected candidate")}
              </Button>
            )}
          </>
        )}
      </details>
      <section aria-label={t("Dependency changes")}>
        <h5 className="font-semibold">{t("Dependency changes")}</h5>
        <table className="w-full text-start text-small">
          <thead>
            <tr>
              <th className="text-start">{t("Change")}</th>
              <th className="text-start">{t("Dependency")}</th>
            </tr>
          </thead>
          <tbody>
            {diff.map((item) => (
              <tr key={item.key}>
                <td>{changeWord[item.change] ?? item.change}</td>
                <td className="break-all">{item.label}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      {errors.length > 0 && (
        <section aria-label={t("Draft validation")} className="text-danger">
          <h5 className="font-semibold">{t("Resolve before saving")}</h5>
          <ul className="list-inside list-disc">
            {[...new Set(errors)].map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </section>
      )}
      <p className="text-small">
        {dirty ? t("Unsaved draft") : t("Draft matches its starting revision")}.{" "}
        {t("Saving pins context {{context}}.", { context: base.session.contextRecordId })}
      </p>
      <div>
        <Button
          variant="primary"
          disabled={
            busy || disabled || !!savedId || (!pending && (errors.length > 0 || drifted || !dirty))
          }
          onClick={save}
        >
          {busy
            ? t("Saving or loading…")
            : pending
              ? t("Retry same save")
              : t("Save candidate revision")}
        </Button>
      </div>
    </section>
  );
}
